import {
  Stack, Typography, Paper, Box, Chip,
  Table, TableHead, TableBody, TableRow, TableCell,
  Alert, Button, TextField, MenuItem,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Action } from "../../../util/func";
import { getMaterialFromIdSearchPayload, searchedMaterialRows } from "../../../schema/console/material";
import { DataLink, MasterLink } from "../../../components/links/EntityLinks";
import { ParseMaterial, rangesToString } from "../../../util/Materialperser";
import { IsAnyDeleted } from "../../../util/DataExtensions";
import { MaterialMasterBase } from "../../../schema/generated/MaterialMaster";
import { EmptyState } from "../../../components/EmptyState";
import { PageContext } from "../PageContext";
import { StageKey, lastStepLink, materialIdOfQr, materialStage, remainingRatioOfQr } from "./helpers";
import { formatRatioAsBagsKg } from "../../../util/consumption";

const MAX_RENDER_ROWS = 300;

export function MaterialSearchListPage({ pageContext, defaultParams, materials, onUpdate, materialMasters, materialsById }: { pageContext: PageContext, defaultParams?: string, materials?: searchedMaterialRows | null, onUpdate: Action<getMaterialFromIdSearchPayload>, materialMasters: MaterialMasterBase[], materialsById: Map<number, MaterialMasterBase> }) {
  const [stringParams, setStringParams] = useState<string>(defaultParams ?? "");
  const [materialId, setMaterialId] = useState<number | "">("");
  const [status, setStatus] = useState<StageKey | "all">("all");
  const navigate = useNavigate();

  const doSearch = () => {
    const targetRanges = ParseMaterial(stringParams);
    if (targetRanges.length > 0) {
      onUpdate({ targetRanges });
      navigate(`?searchParams=${rangesToString(targetRanges)}`, { replace: true });
    } else {
      onUpdate({ targetRanges: [] });
      navigate(".", { replace: true });
    }
  };

  const materialName = (mid: number): string => materialsById.get(mid)?.name ?? `#${mid}`;
  const selectableMaterials = useMemo(
    () => materialMasters.filter((m) => m.deleted_at == null).sort((a, b) => a.display_order - b.display_order),
    [materialMasters],
  );

  // 材料(マスタ)フィルタ後の集合。
  const allRows = materials ?? [];
  const materialFiltered = useMemo(
    () => materialId === "" ? allRows : allRows.filter((m) => materialIdOfQr(m) === materialId),
    [allRows, materialId],
  );

  // 表示行 = 材料フィルタ後にステータス絞り込みも適用した集合。結果概要はこの集合(ステータス絞り込み後)で算出する。
  const tableRows = [...materialFiltered]
    .sort((a, b) => a.id - b.id)
    .filter((m) => status === "all" || materialStage(m).key === status);

  const shippedCount = tableRows.filter((m) => !IsAnyDeleted(m.Lot, m.Lot?.CarryOut)).length;
  const carriedInCount = tableRows.filter((m) => !IsAnyDeleted(m.CarryIn, m.CarryIn?.Work)).length;
  const plannedCount = tableRows.filter((m) => !IsAnyDeleted(m.Construction, m.Construction?.ConstructionPlan, m.Construction?.ConstructionPlan?.Work)).length;
  const consumedCount = tableRows.filter((m) => materialStage(m).key === "consumed").length;
  const partialCount = tableRows.filter((m) => materialStage(m).key === "partial").length;

  return (
    <Stack p={3} gap={2} maxWidth={1400} mx="auto">
      <Typography variant="h4" fontWeight="bold">材料検索</Typography>
      <Alert severity="info" sx={{ py: 0.5 }}>
        材料QR(個別シリアル)を発行〜消費のライフサイクル状態で絞り込み、各QRの最終工程と現在の状態を確認できます。
        材料番号範囲は 1-50, 51+10(パレット), 1,5,9 等が使えます。
      </Alert>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
          <TextField size="small" label="材料番号の範囲" sx={{ minWidth: 320 }}
            value={stringParams} onChange={(e) => setStringParams(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { doSearch(); } }}
            placeholder="例: 1-50, 51+10" />
          <Button variant="contained" onClick={doSearch}>検索</Button>
          <TextField select size="small" label="材料" sx={{ minWidth: 220 }}
            value={materialId} onChange={(e) => setMaterialId(e.target.value === "" ? "" : Number(e.target.value))}>
            <MenuItem value="">すべて</MenuItem>
            {selectableMaterials.map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="ステータス" sx={{ minWidth: 200 }}
            value={status} onChange={(e) => {
              const v = e.target.value;
              if (v === "all" || v === "issued" || v === "shipped" || v === "in_stock" || v === "partial" || v === "planned" || v === "consumed") {
                setStatus(v);
              }
            }}>
            <MenuItem value="all">すべて</MenuItem>
            <MenuItem value="issued">発行のみ（未出荷）</MenuItem>
            <MenuItem value="shipped">出荷済（未搬入）</MenuItem>
            <MenuItem value="in_stock">在庫（搬入済・未消費）</MenuItem>
            <MenuItem value="partial">残量あり（一部消費）</MenuItem>
            <MenuItem value="planned">施工計画済（施工未完了）</MenuItem>
            <MenuItem value="consumed">消費済（施工完了）</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight="bold" mb={1}>結果概要</Typography>
        <Stack direction="row" gap={2} flexWrap="wrap">
          <Chip label={`総数: ${tableRows.length}`} />
          <Chip label={`出荷済: ${shippedCount}`} color="primary" variant="outlined" />
          <Chip label={`搬入済: ${carriedInCount}`} color="info" variant="outlined" />
          <Chip label={`施工計画済: ${plannedCount}`} color="warning" variant="outlined" />
          <Chip label={`残量あり: ${partialCount}`} color="warning" variant="outlined" />
          <Chip label={`消費済: ${consumedCount}`} color="default" variant="outlined" />
        </Stack>
      </Paper>

      <Paper variant="outlined">
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>材料ID</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>材料</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>発行</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>ロット</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>搬入</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>最終工程</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>残量</TableCell>
                <TableCell sx={{ fontWeight: "bold", width: 130 }}>状態</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows.slice(0, MAX_RENDER_ROWS).map((m) => {
                const lot = IsAnyDeleted(m.Lot, m.Lot?.CarryOut) ? null : m.Lot;
                const carryIn = IsAnyDeleted(m.CarryIn, m.CarryIn?.Work) ? null : m.CarryIn;
                const stage = materialStage(m);
                const mid = materialIdOfQr(m);
                return (
                  <TableRow key={m.id} hover>
                    <TableCell><DataLink type="material-qr" id={m.id} /></TableCell>
                    <TableCell>{mid != null ? <MasterLink type="material" id={mid} name={materialName(mid)} /> : "-"}</TableCell>
                    <TableCell>{m.Issuance ? <DataLink type="issuance" id={m.Issuance.id} /> : "-"}</TableCell>
                    <TableCell>{lot?.CarryOut ? <DataLink type="lot" id={lot.id} parentId={lot.CarryOut.id} /> : "-"}</TableCell>
                    <TableCell>{carryIn ? <DataLink type="carry-in" id={carryIn.id} /> : "-"}</TableCell>
                    <TableCell>{lastStepLink(m)}</TableCell>
                    <TableCell>{stage.key === "partial" ? formatRatioAsBagsKg(remainingRatioOfQr(m), 0) : "-"}</TableCell>
                    <TableCell><Chip size="small" label={stage.label} color={stage.color} variant="outlined" /></TableCell>
                  </TableRow>
                );
              })}
              {tableRows.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>該当データなし</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          {tableRows.length > MAX_RENDER_ROWS && (
            <EmptyState message={`（表示は先頭 ${MAX_RENDER_ROWS} 件まで。さらに絞り込んでください）`} py={1} />
          )}
        </Box>
      </Paper>
    </Stack>
  );
}
